import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, forkJoin, merge, of } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { SaleService, SaleDTO, SalePipelineStatus, SaleStatus, SaleType, SalePipelineStatusEnum } from '../../services/sale.service';
import { AuthService } from '../../services/auth.service';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { SaleReadComponent } from './sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { SubscriptionService } from '../../services/subscription.service';
import type { SubscriptionDTO } from '../../core/models';
import type { ChargeDTO, CollectionMethodEnumValue, DelinquencyStatusEnumValue } from '../../core/models';
import { CollectionMethodEnum, EntityState } from '../../core/models';
import { parseApiDateTime } from '../../core/date-utils';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { ListSaleFilterSpec } from '@/src/services/service-common';
import { catchError, finalize, map } from 'rxjs/operators';
import type { WrapperPageableResponse } from '@/src/services/service-common';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';

interface KanbanColumnState {
  items: SaleDTO[];
  page: number;
  totalItems: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
}

@Component({
  selector: 'app-sales-list',
  templateUrl: './sales-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        position: sticky;
        top: 0;
        z-index: 20;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .sales-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
        overflow: visible;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-tbody > tr > td:first-child {
        overflow: visible;
      }
      .sale-stamped-badge {
        display: inline-grid;
        place-items: center;
        line-height: 0;
      }
      .sale-stamped-badge i.pi {
        display: block;
        margin: 0;
        padding: 0;
        line-height: 1;
        width: 1em;
        height: 1em;
      }
      .sale-stamped-badge i.pi::before {
        display: block;
        line-height: 1;
      }
      :host ::ng-deep .sales-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep .sales-primeng-table .sales-col-subscription {
        width: 7rem;
        max-width: 7rem;
      }
      :host ::ng-deep .sales-primeng-table .sales-col-validity {
        width: 12rem;
        max-width: 12rem;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .sales-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important; /* sky-100 */
        border-color: rgb(14 165 233) !important; /* sky-500 */
        color: rgb(2 132 199) !important; /* sky-600 */
      }
      .sales-kanban-board .cdk-drop-list {
        min-height: 200px;
      }
      .sales-kanban-card.cdk-drag-preview {
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
        border-radius: 0.75rem;
      }
      .sales-kanban-card.cdk-drag-animating {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    DrawerModule,
    DragDropModule,
    ScrollingModule,
    SaleReadComponent,
    CustomerReadComponent,
    SubscriptionReadComponent
  ]
})
export class SalesListComponent {
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  readonly canExportSales = this.authService.hasRole('SALE_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportSales) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay ventas para exportar con los filtros actuales';
    }
    return 'Descargar todas las ventas filtradas en CSV';
  }

  /** Drawer de detalle (detalle vía GET /sales/{id}) */
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);

  /** Drawer de detalle de cliente (desde venta) */
  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  /** Drawer de detalle de suscripción (desde venta) */
  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);

  publicHashLoading = signal(false);

  sales = signal<SaleDTO[]>([]);
  loading = signal(false);
  /** True después de la primera carga. Distingue primera carga (skeleton) de actualizaciones (solo loading de PrimeNG). */
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  sortField = signal('createdAt');
  sortOrder = signal(-1);

  /** Vista tabla o embudo Kanban por `processStatus` */
  viewMode = signal<'table' | 'kanban'>('table');
  /** Registros por página en cada columna del Kanban. */
  readonly kanbanPageSize = 20;
  /** Tarjeta en movimiento (PUT en curso). */
  kanbanMovePendingId = signal<number | null>(null);
  /** Estado paginado independiente por columna del embudo. */
  kanbanColumnStates = signal<Record<SalePipelineStatus, KanbanColumnState>>(this.emptyKanbanColumnStates());
  /** Placeholders de skeleton por columna Kanban. */
  kanbanColumnSkeletonRows = [1, 2, 3];

  /** Filas para el skeleton de la primera carga */
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    number: [''],
    q: [''],
    status: [this.allFilterValue],
    state: [EntityState.ACTIVE],
    isStamped: [this.allFilterValue],
    createdAtRange: this.fb.control<Date[] | null>(null)
  });

  hasActiveFilters = signal(false);

  /** Filtro por estado del ciclo de cobro (`Sale.status`, mismo enum que factura en API). */
  processStatusOptions: { value: SaleStatus; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  statusFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    ...this.processStatusOptions
  ];

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  stampedFilterOptions = [
    { value: this.allFilterValue, label: 'Todas' },
    { value: 'true', label: 'Sí' },
    { value: 'false', label: 'No' }
  ];

  pipelineStatusOptions: { value: SalePipelineStatus; label: string }[] = [
    { value: 'ACCEPTED', label: 'Aceptado' },
    { value: 'CONTRACT_ATTACHMENT', label: 'Contrato/Anexo' },
    { value: 'OC', label: 'OC' },
    { value: 'INVOICED', label: 'Facturado' },
    { value: 'INVOICE_IMPORTED', label: 'Factura Ingresada' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'LOST', label: 'Perdido' }
  ];

  saleTypeOptions: { value: SaleType; label: string }[] = [
    { value: 'ONE_TIME', label: 'Venta única' },
    { value: 'SUBSCRIPTION', label: 'Suscripción' }
  ];

  constructor() {
    this.filtersForm.controls.number.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    merge(
      this.filtersForm.controls.status.valueChanges,
      this.filtersForm.controls.state.valueChanges,
      this.filtersForm.controls.isStamped.valueChanges,
      this.filtersForm.controls.createdAtRange.valueChanges
    ).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());

    this.loadSales();
  }

  loadSales(): void {
    if (this.viewMode() === 'kanban') {
      this.loadKanban(true);
      return;
    }
    this.loadTableSales();
  }

  private loadTableSales(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.saleService.listSales(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.sales.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.totalPages.set(response.page.totalPages ?? 0);
            this.pageSize.set(response.page.pageSize ?? 20);
            this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          } else {
            this.totalItems.set(response.body?.length ?? 0);
            this.totalPages.set(this.getTotalPages());
          }
        } else {
          this.error.set(response.message || 'Error al cargar ventas');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading sales:', err);
      }
    });
  }

  /** Carga la primera página de cada columna del Kanban en paralelo. */
  private loadKanban(reset: boolean): void {
    if (reset) {
      this.loading.set(true);
      this.error.set(null);
      this.kanbanColumnStates.set(this.emptyKanbanColumnStates());
      this.kanbanColumnStates.update((states) => {
        const next = { ...states };
        for (const { value } of this.pipelineStatusOptions) {
          next[value] = { ...next[value], loading: true };
        }
        return next;
      });
    }

    const requests = this.pipelineStatusOptions.map(({ value: status }) =>
      this.saleService
        .listSales(
          this.buildKanbanColumnSpec(status),
          { page: 0, size: this.kanbanPageSize, sort: ['createdAt,desc'] }
        )
        .pipe(
          map((response) => ({ status, response })),
          catchError((err) => {
            console.error(`Error loading kanban column ${status}:`, err);
            return of({
              status,
              response: {
                ok: false,
                code: 0,
                message: 'Error',
                body: []
              } as WrapperPageableResponse<SaleDTO>
            });
          })
        )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        const next = { ...this.kanbanColumnStates() };
        let hasError = false;

        for (const { status, response } of results) {
          if (response.ok) {
            const items = response.body ?? [];
            const totalItems = response.page?.totalItems ?? items.length;
            const totalPages = response.page?.totalPages ?? (items.length > 0 ? 1 : 0);
            next[status] = {
              items,
              page: 0,
              totalItems,
              loading: false,
              loadingMore: false,
              hasMore: totalPages > 1
            };
          } else {
            hasError = true;
            next[status] = {
              ...next[status],
              items: [],
              page: -1,
              totalItems: 0,
              loading: false,
              loadingMore: false,
              hasMore: false
            };
          }
        }

        this.kanbanColumnStates.set(next);
        if (hasError) {
          this.error.set('Error al cargar algunas columnas del Kanban');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading kanban:', err);
      }
    });
  }

  loadMoreKanbanColumn(status: SalePipelineStatus): void {
    const col = this.kanbanColumnStates()[status];
    if (!col || col.loading || col.loadingMore || !col.hasMore) return;

    const nextPage = col.page + 1;
    const spec = this.buildKanbanColumnSpec(status);

    this.kanbanColumnStates.update((states) => ({
      ...states,
      [status]: { ...states[status], loadingMore: true }
    }));

    this.saleService
      .listSales(spec, { page: nextPage, size: this.kanbanPageSize, sort: ['createdAt,desc'] })
      .subscribe({
        next: (response) => {
          if (response.ok) {
            const batch = response.body ?? [];
            const previousCount = col.items.length;
            const existingIds = new Set(col.items.map((s) => s.id));
            const merged = [...col.items];
            for (const sale of batch) {
              if (sale.id != null && !existingIds.has(sale.id)) {
                merged.push(sale);
              }
            }

            const totalItems = response.page?.totalItems ?? col.totalItems;
            const totalPages = response.page?.totalPages ?? 0;
            const exhausted = batch.length === 0 || merged.length === previousCount;
            const noMorePages = totalPages > 0 && nextPage + 1 >= totalPages;

            this.kanbanColumnStates.update((states) => ({
              ...states,
              [status]: {
                ...states[status],
                items: merged,
                page: nextPage,
                totalItems,
                loadingMore: false,
                hasMore: !exhausted && !noMorePages && merged.length < totalItems
              }
            }));
          } else {
            this.kanbanColumnStates.update((states) => ({
              ...states,
              [status]: { ...states[status], loadingMore: false, hasMore: false }
            }));
            this.toastr.error(response.message || 'No se pudieron cargar más ventas');
          }
        },
        error: (err) => {
          this.kanbanColumnStates.update((states) => ({
            ...states,
            [status]: { ...states[status], loadingMore: false }
          }));
          this.toastr.error(err?.error?.message || err?.message || 'Error al cargar más ventas');
          console.error(`Error loading more kanban column ${status}:`, err);
        }
      });
  }

  onKanbanColumnScroll(event: Event, status: SalePipelineStatus): void {
    const el = event.target as HTMLElement;
    const threshold = 120;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMoreKanbanColumn(status);
    }
  }

  kanbanColumnState(status: SalePipelineStatus): KanbanColumnState {
    return this.kanbanColumnStates()[status];
  }

  /** Lazy load para la tabla PrimeNG */
  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.viewMode() === 'kanban') return;
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : size
        ? Math.floor((event.first ?? 0) / size)
        : 0;

    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) return;

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadSales();
  }

  setViewMode(mode: 'table' | 'kanban'): void {
    if (this.viewMode() === mode) return;
    this.viewMode.set(mode);
    if (mode === 'kanban') {
      this.currentPage.set(0);
    }
    this.loadSales();
  }

  private createEmptyKanbanColumnState(): KanbanColumnState {
    return {
      items: [],
      page: -1,
      totalItems: 0,
      loading: false,
      loadingMore: false,
      hasMore: true
    };
  }

  private emptyKanbanColumnStates(): Record<SalePipelineStatus, KanbanColumnState> {
    const keys = Object.values(SalePipelineStatusEnum) as SalePipelineStatus[];
    const r = {} as Record<SalePipelineStatus, KanbanColumnState>;
    for (const k of keys) {
      r[k] = this.createEmptyKanbanColumnState();
    }
    return r;
  }

  onKanbanDrop(event: CdkDragDrop<SaleDTO[]>, targetStatus: SalePipelineStatus): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.kanbanColumnStates.update((c) => ({ ...c }));
      return;
    }

    const moved = event.previousContainer.data[event.previousIndex];
    const sourceStatus = (moved.processStatus ?? 'OC') as SalePipelineStatus;
    if (sourceStatus === targetStatus) return;

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    moved.processStatus = targetStatus;
    this.kanbanColumnStates.update((states) => {
      const source = states[sourceStatus];
      const target = states[targetStatus];
      return {
        ...states,
        [sourceStatus]: {
          ...source,
          totalItems: Math.max(0, source.totalItems - 1)
        },
        [targetStatus]: {
          ...target,
          totalItems: target.totalItems + 1
        }
      };
    });

    if (moved.id == null) return;

    this.kanbanMovePendingId.set(moved.id);
    this.saleService
      .patchSaleProcessStatus(moved.id, targetStatus)
      .pipe(finalize(() => this.kanbanMovePendingId.set(null)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.toastr.success('Embudo actualizado');
          } else {
            this.revertKanbanMove(moved, sourceStatus, targetStatus, event);
            this.toastr.error(r.message || 'No se pudo actualizar el embudo');
          }
        },
        error: (err) => {
          this.revertKanbanMove(moved, sourceStatus, targetStatus, event);
          this.toastr.error(err?.error?.message || err?.message || 'Error al actualizar el embudo');
        }
      });
  }

  private revertKanbanMove(
    sale: SaleDTO,
    sourceStatus: SalePipelineStatus,
    targetStatus: SalePipelineStatus,
    event: CdkDragDrop<SaleDTO[]>
  ): void {
    sale.processStatus = sourceStatus;
    transferArrayItem(
      event.container.data,
      event.previousContainer.data,
      event.currentIndex,
      event.previousIndex
    );
    this.kanbanColumnStates.update((states) => {
      const source = states[sourceStatus];
      const target = states[targetStatus];
      return {
        ...states,
        [sourceStatus]: {
          ...source,
          totalItems: source.totalItems + 1
        },
        [targetStatus]: {
          ...target,
          totalItems: Math.max(0, target.totalItems - 1)
        }
      };
    });
  }

  isKanbanDragDisabled(sale: SaleDTO): boolean {
    return this.kanbanMovePendingId() === sale.id;
  }

  /**
   * Footer Kanban según `Sale.delinquencyStatus` (dominio API / `DelinquencyStatus`).
   * Borradores no se consideran vencidos; OVERDUE = vencida (rojo) solo fuera de DRAFT.
   */
  getKanbanDelinquencyFooter(sale: SaleDTO): { text: string; tone: 'red' | 'green' | 'amber' | 'neutral' } {
    if (sale.status === 'CANCELLED' || sale.status === 'VOID') {
      return { text: 'Cancelada', tone: 'neutral' };
    }
    if (sale.status === 'DRAFT') {
      return { text: 'Borrador', tone: 'neutral' };
    }
    if (sale.status === 'PAID' || (sale.amountDue ?? 0) <= 0) {
      return { text: 'Sin saldo pendiente', tone: 'neutral' };
    }

    const d = sale.delinquencyStatus ?? 'CURRENT';
    switch (d) {
      case 'CURRENT':
        return { text: 'Vigente', tone: 'green' };
      case 'OVERDUE':
        return { text: 'Vencida', tone: 'red' };
      default:
        return { text: String(d), tone: 'neutral' };
    }
  }

  onPageChange(page: number): void {
    const total = this.totalPages();
    if (page < 0) return;
    if (total > 0 && page >= total) return;
    this.currentPage.set(page);
    this.loadSales();
  }

  onPageSizeChange(size: number): void {
    if (!size || size === this.pageSize()) return;
    this.pageSize.set(size);
    this.currentPage.set(0);
    this.loadSales();
  }

  /**
   * True mientras hubo un arrastre real (cdkDragStarted). El `click` posterior al soltar se ignora;
   * se limpia en el siguiente tick para no bloquear el siguiente clic en otra tarjeta.
   */
  private kanbanDragGestureActive = false;

  onKanbanDragStarted(): void {
    this.kanbanDragGestureActive = true;
  }

  onKanbanDragEnded(): void {
    setTimeout(() => {
      this.kanbanDragGestureActive = false;
    }, 0);
  }

  /** Clic en la tarjeta: abre detalle si no acaba de completarse un drag en la misma interacción. */
  onKanbanCardClick(sale: SaleDTO): void {
    if (this.kanbanDragGestureActive) return;
    this.openSaleDetails(sale);
  }

  openSaleDetails(sale: SaleDTO): void {
    if (!sale?.id) return;
    this.loadSaleDetails(sale.id);
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    if (!saleId) return;
    this.loadSaleDetails(saleId);
  }

  private loadSaleDetails(saleId: number): void {
    this.isDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.detailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeDetails();
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.detailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeDetails();
        console.error('Error loading sale detail:', err);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  openCustomerDetails(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: (err) => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSubscriptionDetails(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: (err) => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeSubscriptionDetails();
        console.error('Error loading subscription by id:', err);
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  /**
   * Genera un hash público de la venta y abre la vista pública en una nueva pestaña.
   */
  generatePublicHashAndOpen(event?: MouseEvent): void {
    event?.stopPropagation();
    this.toastr.info('Enlace público no disponible para ventas.');
  }

  editSale(sale: SaleDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!sale?.id) return;
    this.router.navigate(['/sales', sale.id]);
  }

  /** Recarga el listado sin resetear filtros ni página. */
  refreshTable(): void {
    this.loadSales();
  }

  exportCsv(): void {
    if (!this.canExportSales || this.exporting()) return;
    this.exporting.set(true);
    this.saleService.exportSalesCsv(this.buildSpecFromFilters(), ['createdAt,desc']).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de ventas descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de ventas';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
        console.error('Error exporting sales CSV:', err);
      }
    });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'ventas.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadSales();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      {
        number: '',
        q: '',
        status: this.allFilterValue,
        state: EntityState.ACTIVE,
        isStamped: this.allFilterValue,
        createdAtRange: null
      },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.number?.trim() ||
        raw.q?.trim() ||
        this.isSpecificFilter(raw.status) ||
        raw.state !== EntityState.ACTIVE ||
        this.isSpecificFilter(raw.isStamped) ||
        this.hasCompleteDateRange(raw.createdAtRange)
      )
    );
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }

  private hasCompleteDateRange(range: Date[] | null | undefined): boolean {
    return !!range?.[0] && !!range?.[1];
  }

  /** Etiqueta de `Sale.status` (ciclo de cobro). */
  getStatusLabel(status: SaleStatus | string | undefined): string {
    if (!status) return '—';
    return this.processStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  /** Morosidad (`Sale.delinquencyStatus`). */
  getDelinquencyLabel(d: DelinquencyStatusEnumValue | string | undefined): string {
    if (!d || d === 'CURRENT') return '';
    const map: Record<string, string> = {
      OVERDUE: 'Vencida'
    };
    return map[d] ?? d;
  }

  getDelinquencySeverity(
    d: DelinquencyStatusEnumValue | string | undefined
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | null {
    if (!d || d === 'CURRENT') return null;
    if (d === 'OVERDUE') return 'danger';
    return 'secondary';
  }

  getPipelineStatusLabel(s: SalePipelineStatus): string {
    return this.pipelineStatusOptions.find((o) => o.value === s)?.label ?? s;
  }

  getSaleTypeLabel(k: SaleType): string {
    return this.saleTypeOptions.find((o) => o.value === k)?.label ?? k;
  }

  private readonly collectionMethodLabels: Record<CollectionMethodEnumValue, string> = {
    [CollectionMethodEnum.LINK]: 'Liga de pago',
    [CollectionMethodEnum.RECURRING_CHARGE]: 'Cobro recurrente',
    [CollectionMethodEnum.MANUAL]: 'Manual',
    [CollectionMethodEnum.MERCHANT_INITIATED]: 'Cobro bajo demanda'
  };

  getCollectionMethodLabel(method: CollectionMethodEnumValue | string | undefined): string {
    if (!method) return '—';
    return this.collectionMethodLabels[method as CollectionMethodEnumValue] ?? String(method);
  }

  /**
   * Vencimiento del cobro: collectionStartDate + daysUntilExpiry.
   * Sin plazo, vence el mismo día de inicio.
   */
  saleCollectionDueDate(sale: SaleDTO): Date | null {
    return this.collectionDueDate(this.saleCollectionStartDate(sale), sale.daysUntilExpiry);
  }

  /**
   * `collectionStartDate` es un instante (`OffsetDateTime` en UTC); se lleva a la zona del navegador
   * igual que `paidAt`, para que ambos caigan en el mismo día calendario.
   */
  saleCollectionStartDate(sale: SaleDTO): Date | null {
    return parseApiDateTime(sale.collectionStartDate);
  }

  collectionDueDate(start: Date | null, daysUntilExpiry: number | null | undefined): Date | null {
    if (!start) return null;
    const days = daysUntilExpiry != null && Number.isFinite(Number(daysUntilExpiry))
      ? Math.trunc(Number(daysUntilExpiry))
      : 0;
    const due = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    due.setDate(due.getDate() + days);
    return due;
  }

  isDueDateExpired(due: Date | null): boolean {
    return this.daysOverdue(due) > 0;
  }

  daysOverdue(due: Date | null): number {
    if (!due) return 0;
    const today = new Date();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((todayDay.getTime() - dueDay.getTime()) / 86_400_000);
    return days > 0 ? days : 0;
  }

  overdueTooltip(due: Date | null): string {
    const days = this.daysOverdue(due);
    if (days <= 0) return '';
    return days === 1 ? 'Vencida hace 1 día' : `Vencida hace ${days} días`;
  }

  saleCollectionValidity(sale: SaleDTO) {
    return this.collectionValidity(
      this.saleCollectionStartDate(sale),
      this.saleCollectionDueDate(sale),
      this.isSalePaid(sale),
      parseApiDateTime(sale.paidAt)
    );
  }

  /** Avance de la vigencia del cobro: verde 0–70 %, amarillo 71–99 %, rojo 100 %. Pagada: barra vacía (gris del 0 %). */
  collectionValidity(start: Date | null, due: Date | null, paid = false, paidAt: Date | null = null): {
    start: Date;
    due: Date;
    percent: number;
    barClass: string;
    expired: boolean;
    remainingLabel: string;
    paidAt: Date | null;
    /** Posición de `paidAt` entre inicio y vencimiento, 0–100. */
    paidMarkerPercent: number | null;
  } | null {
    if (!start || !due) return null;
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const span = dueDay - startDay;
    let percent: number;
    if (paid) {
      percent = 0;
    } else if (today >= dueDay) {
      percent = 100;
    } else if (today <= startDay || span <= 0) {
      percent = 0;
    } else {
      percent = Math.min(99, Math.floor(((today - startDay) / span) * 100));
    }
    const barClass = percent >= 100 ? 'bg-red-500' : percent > 70 ? 'bg-amber-400' : 'bg-emerald-500';
    const daysLeft = Math.round((dueDay - today) / 86_400_000);
    const absDays = Math.abs(daysLeft);
    const daysText = absDays === 1 ? '1 día' : `${absDays} días`;
    const remainingLabel = paid
      ? 'Pagada'
      : daysLeft < 0
        ? `Vencido hace ${daysText}`
        : daysLeft === 0
          ? 'Vence hoy'
          : `Vence en ${daysText}`;
    let paidMarkerPercent: number | null = null;
    if (paidAt) {
      const paidDay = new Date(paidAt.getFullYear(), paidAt.getMonth(), paidAt.getDate()).getTime();
      if (span <= 0) {
        paidMarkerPercent = paidDay <= startDay ? 0 : 100;
      } else {
        paidMarkerPercent = Math.min(100, Math.max(0, ((paidDay - startDay) / span) * 100));
      }
    }
    return {
      start,
      due,
      percent,
      barClass,
      expired: !paid && this.isDueDateExpired(due),
      remainingLabel,
      paidAt,
      paidMarkerPercent
    };
  }

  /** Icono según `Sale.status` o estado inactivo (`Sale.state`). */
  isSaleInactive(sale: SaleDTO | null | undefined): boolean {
    return sale?.state === EntityState.INACTIVE;
  }

  getStatusIcon(sale: SaleDTO | null | undefined): string {
    if (this.isSaleInactive(sale)) return 'pi pi-trash';
    if (this.isSalePaid(sale)) return 'pi pi-dollar';
    const status = sale?.status;
    if (!status) return 'pi pi-receipt';
    switch (status) {
      case 'DRAFT':
        return 'pi pi-file-edit';
      case 'ISSUED':
        return 'pi pi-send';
      case 'PARTIALLY_PAID':
        return 'pi pi-wallet';
      case 'PAID':
        return 'pi pi-check-circle';
      case 'CANCELLED':
      case 'VOID':
        return 'pi pi-ban';
      case 'UNCOLLECTIBLE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-receipt';
    }
  }

  /** Venta timbrada / con CFDI vigente (listado puede traer `invoice` y no `isStamped`). */
  isSaleStamped(sale: SaleDTO | null | undefined): boolean {
    if (!sale) return false;
    const jacksonAlias = (sale as SaleDTO & { stamped?: boolean }).stamped;
    return sale.isStamped === true
      || jacksonAlias === true
      || sale.invoice != null
      || sale.invoiceId != null;
  }

  /** Venta cobrada (sin saldo pendiente). Facturada tiene prioridad visual sobre pagada. */
  isSalePaid(sale: SaleDTO | null | undefined): boolean {
    if (!sale || sale.status === 'DRAFT' || sale.status === 'CANCELLED' || sale.status === 'VOID') return false;
    return sale.status === 'PAID' || (sale.amountDue ?? 0) <= 0;
  }

  /** Colores del círculo del icono según `Sale.status` o estado inactivo (`Sale.state`). */
  getStatusIconClasses(sale: SaleDTO | null | undefined): string {
    const base = 'shrink-0 h-10 w-10 rounded-full flex items-center justify-center';
    if (this.isSaleInactive(sale)) return `${base} bg-red-100 text-red-600`;
    if (this.isSalePaid(sale)) return `${base} bg-emerald-100 text-emerald-600`;
    const status = sale?.status;
    if (!status) return `${base} bg-slate-100 text-slate-600`;
    switch (status) {
      case 'DRAFT':
        return `${base} bg-sky-100 text-sky-600`;
      case 'ISSUED':
        return `${base} bg-violet-100 text-violet-600`;
      case 'PARTIALLY_PAID':
        return `${base} bg-amber-100 text-amber-600`;
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-600`;
      case 'CANCELLED':
      case 'VOID':
        return `${base} bg-slate-200 text-slate-600`;
      case 'UNCOLLECTIBLE':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  /** Filtro por columna Kanban: API kaczmarzyk usa `processStatus.name` como query param por defecto. */
  private buildKanbanColumnSpec(status: SalePipelineStatus): ListSaleFilterSpec {
    return {
      ...this.buildSpecFromFilters(),
      processStatus: status,
      'processStatus.name': status
    };
  }

  private buildSpecFromFilters(): ListSaleFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ListSaleFilterSpec = {};
    const number = value.number?.trim();
    const q = value.q?.trim();
    if (number) {
      spec['number[lk]'] = number;
    }
    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.status)) {
      spec.status = value.status;
    }
    if (this.isSpecificFilter(value.state)) {
      spec.state = value.state;
    } else {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }
    if (this.isSpecificFilter(value.isStamped)) {
      spec.isStamped = value.isStamped;
    }
    this.applyCreatedAtRange(spec, value.createdAtRange);
    return spec;
  }

  private applyCreatedAtRange(spec: ListSaleFilterSpec, range: Date[] | null | undefined): void {
    if (!this.hasCompleteDateRange(range) || !range?.[0] || !range[1]) return;
    const from = new Date(range[0]);
    from.setHours(0, 0, 0, 0);
    const toExclusive = new Date(range[1]);
    toExclusive.setHours(0, 0, 0, 0);
    toExclusive.setDate(toExclusive.getDate() + 1);
    spec['createdAt[gte]'] = from.toISOString();
    spec['createdAt[lt]'] = toExclusive.toISOString();
  }

  getTotalPages(): number {
    const total = this.totalPages();
    if (total > 0) return total;
    return Math.ceil(Math.max(0, this.totalItems()) / this.pageSize()) || 1;
  }
}
